import React from 'react';
import { useState } from 'react';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // In a real implementation, this would send the form data to a server
    console.log('Form submitted:', formData);
    alert('Thank you for your message! We will get back to you soon.');
    setFormData({
      name: '',
      company: '',
      email: '',
      phone: '',
      service: '',
      message: ''
    });
  };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container mx-auto px-4 py-16">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4 text-center">Contact Us</h1>
          <p className="text-xl text-center max-w-3xl mx-auto">
            Ready to transform your financial service marketing with the power of AI and automation?
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="section bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold mb-6">Get in Touch</h2>
              <p className="mb-8">
                Fill out the form below to discuss how DLDV Enterprises can help you leverage AI and automation to achieve exceptional results for your financial service business.
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-color"
                  />
                </div>
                
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">Company *</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-color"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-color"
                  />
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-color"
                  />
                </div>
                
                <div>
                  <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-1">Service Interest *</label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-color"
                  >
                    <option value="">Select a service</option>
                    <option value="placement">Placement Agent Services</option>
                    <option value="introductions">Network Introductions</option>
                    <option value="events">Event Hosting & Management</option>
                    <option value="documents">Document Creation</option>
                    <option value="websites">Website Development</option>
                    <option value="ai">AI and Automation Integration</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-color"
                  ></textarea>
                </div>
                
                <div>
                  <button type="submit" className="btn-primary">
                    Send Message
                  </button>
                </div>
              </form>
            </div>
            
            <div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <h2 className="text-3xl font-bold mb-6">Contact Information</h2>
                
                <div className="mb-8">
                  <h3 className="text-xl font-semibold mb-2">Office Location</h3>
                  <p>
                    DLDV Enterprises BV<br />
                    Netherlands
                  </p>
                </div>
                
                <div className="mb-8">
                  <h3 className="text-xl font-semibold mb-2">Email</h3>
                  <p className="text-accent-color">contact@dldventerprises.com</p>
                </div>
                
                <div className="mb-8">
                  <h3 className="text-xl font-semibold mb-2">Phone</h3>
                  <p>+31 (0) XX XXX XXXX</p>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold mb-2">Connect With Us</h3>
                  <div className="flex space-x-4">
                    <a href="https://www.linkedin.com/in/dirkdevlaam/" target="_blank" rel="noopener noreferrer" className="text-primary-color hover:text-accent-color transition">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
              
              <div className="mt-8">
                <img src="/images/dldv_primary.svg" alt="DLDV Enterprises" className="w-full max-w-xs mx-auto" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Frequently Asked Questions</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="card">
              <h3 className="text-xl font-bold mb-3">How quickly can you implement AI solutions for our firm?</h3>
              <p>
                Implementation timelines vary based on the complexity of your needs, but we typically begin with a quick-win approach that delivers initial results within 4-6 weeks, followed by more comprehensive integration over 3-6 months.
              </p>
            </div>
            
            <div className="card">
              <h3 className="text-xl font-bold mb-3">Do we need technical expertise to work with your services?</h3>
              <p>
                No technical expertise is required on your end. Our team handles all technical aspects of implementation and provides user-friendly interfaces for your team to interact with the systems we develop.
              </p>
            </div>
            
            <div className="card">
              <h3 className="text-xl font-bold mb-3">How do you ensure data security and compliance?</h3>
              <p>
                We adhere to the highest standards of data security and regulatory compliance. All our systems are built with privacy by design principles, and we conduct regular security audits to ensure your data remains protected.
              </p>
            </div>
            
            <div className="card">
              <h3 className="text-xl font-bold mb-3">Can your services be customized for our specific needs?</h3>
              <p>
                Absolutely. We believe in tailored solutions rather than one-size-fits-all approaches. Our process begins with a thorough assessment of your specific needs, challenges, and objectives to create a customized implementation plan.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
